// A draft invoice at Review, changed in place (gap #1): add a line from the price book
// (invoices.addLine), take one off (invoices.removeLine), or change its quantity or price
// (invoices.updateLine). Every write answers with the invoice as the server now has it, so the totals
// and tax are never worked out here; a refusal is shown in the server's words.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { parseMoney } from '../books/model.ts';
import { useSaveKey } from '../shared/SaveButton.tsx';
import { invoiceState, money } from './review-model.ts';
import {
  memberNote,
  needsReason,
  priceNote,
  quantityOk,
  quantityText,
  type EditableInvoice,
  type InvoiceLine,
} from './invoice-lines-model.ts';
import './invoice-lines.css';

const say = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'That didn’t go through. Try again.';
const dollars = (cents: number) => (cents / 100).toFixed(2);

interface Props {
  jobId: string;
  invoice: EditableInvoice;
  /** reviewRights: lines (add, take off and change a draft's lines). */
  rights: { lines: boolean };
  onChanged: (invoice: EditableInvoice) => void;
}

export function InvoiceLines({ jobId, invoice, rights, onChanged }: Props) {
  const [open, setOpen] = useState<{ kind: 'change' | 'remove'; lineId: string } | 'add' | null>(null);
  const done = (inv: EditableInvoice) => {
    setOpen(null);
    onChanged(inv);
  };
  return (
    <div className="invoice inv-edit">
      <div className="row between">
        <h3 className="h4">Invoice {invoice.number}</h3>
        <span className="chip">{invoiceState(invoice)}</span>
      </div>
      <ul className="inv-lines">
        {invoice.lines.map((l) => (
          <li key={l.id}>
            <div className="inv-line">
              <div className="inv-what">
                <span>{l.description}</span>
                {quantityText(l) && <span className="muted small">{quantityText(l)}</span>}
                {priceNote(l) && <span className="muted small">{priceNote(l)}</span>}
                {memberNote(l) && <span className="small member-note">{memberNote(l)}</span>}
                {l.fromEstimate && <span className="muted small">From the signed estimate</span>}
              </div>
              <span className="num">{money(l.totalCents)}</span>
            </div>
            {open && open !== 'add' && open.lineId === l.id ? (
              open.kind === 'change' ? (
                <ChangeLine line={l} onDone={done} onCancel={() => setOpen(null)} />
              ) : (
                <RemoveLine line={l} onDone={done} onCancel={() => setOpen(null)} />
              )
            ) : (
              rights.lines && (
                <div className="row inv-line-actions">
                  {rights.lines && (
                    <button className="btn quiet" onClick={() => setOpen({ kind: 'change', lineId: l.id })}>
                      Change
                    </button>
                  )}
                  {rights.lines && (
                    <button className="btn quiet" onClick={() => setOpen({ kind: 'remove', lineId: l.id })}>
                      Remove
                    </button>
                  )}
                </div>
              )
            )}
          </li>
        ))}
        {invoice.lines.length === 0 && <li className="muted">No lines yet.</li>}
      </ul>
      <table>
        <tbody>
          {invoice.taxes.map((t) => (
            <tr key={t.code} className="muted">
              <td>{t.code}</td>
              <td className="num">{money(t.taxCents)}</td>
            </tr>
          ))}
          <tr className="total">
            <td>Total</td>
            <td className="num">{money(invoice.totalCents)}</td>
          </tr>
        </tbody>
      </table>
      {rights.lines && invoice.memberPricesToApply && <ApplyMemberPrices jobId={jobId} onDone={done} />}
      {rights.lines &&
        (open === 'add' ? (
          <AddLine jobId={jobId} onDone={done} onCancel={() => setOpen(null)} />
        ) : (
          <div className="row">
            <button className="btn" onClick={() => setOpen('add')}>
              Add a line
            </button>
          </div>
        ))}
    </div>
  );
}

function ChangeLine({
  line,
  onDone,
  onCancel,
}: {
  line: InvoiceLine;
  onDone: (inv: EditableInvoice) => void;
  onCancel: () => void;
}) {
  const [quantity, setQuantity] = useState(String(Number(line.quantity)));
  const [price, setPrice] = useState(dollars(line.unitPriceCents));
  const [reason, setReason] = useState(line.overrideReason ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = parseMoney(price);
  const priceChanged = cents !== null && cents !== line.unitPriceCents;
  const qtyChanged = quantityOk(quantity) && Number(quantity) !== Number(line.quantity);
  const wantReason = cents !== null && priceChanged && needsReason(line, cents);
  const ok =
    quantityOk(quantity) &&
    cents !== null &&
    cents >= 0 &&
    (priceChanged || qtyChanged) &&
    (!wantReason || !!reason.trim());
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(
        await client.call<EditableInvoice>('invoices.updateLine', {
          lineId: line.id,
          ...(qtyChanged ? { quantity: quantity.trim() } : {}),
          ...(priceChanged ? { unitPriceCents: cents } : {}),
          ...(wantReason ? { overrideReason: reason.trim() } : {}),
        }),
      );
    } catch (e) {
      setError(say(e));
      setBusy(false);
    }
  };
  return (
    <form
      className="inv-form"
      aria-label={`Change ${line.description}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (ok && !busy) void save();
      }}
    >
      <div className="inv-fields">
        <label className="f">
          Quantity
          <input
            className="field"
            inputMode="decimal"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </label>
        <label className="f">
          Price each
          <input
            className="field"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </label>
      </div>
      {line.bookPriceCents != null && <p className="muted small">List price {money(line.bookPriceCents)}.</p>}
      {wantReason && (
        <label className="f">
          Why the price changes
          <input
            className="field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Goodwill, a matched quote, a second visit…"
          />
        </label>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="btn primary" type="submit" disabled={!ok || busy}>
          {busy ? 'Saving…' : 'Save the line'}
        </button>
        <button className="btn quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function RemoveLine({
  line,
  onDone,
  onCancel,
}: {
  line: InvoiceLine;
  onDone: (inv: EditableInvoice) => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await client.call<EditableInvoice>('invoices.removeLine', { lineId: line.id }));
    } catch (e) {
      setError(say(e));
      setBusy(false);
    }
  };
  return (
    <div className="inv-form" role="group" aria-label={`Take ${line.description} off`}>
      <p>
        Take <b>{line.description}</b> ({money(line.totalCents)}) off the invoice?
      </p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="btn danger" disabled={busy} onClick={() => void remove()}>
          {busy ? 'Taking it off…' : 'Take it off'}
        </button>
        <button className="btn quiet" onClick={onCancel} disabled={busy}>
          Keep it
        </button>
      </div>
    </div>
  );
}

interface BookItem {
  id: string;
  code: string;
  name: string;
  unit: string;
  priceCents: number;
  type: string;
}

function AddLine({
  jobId,
  onDone,
  onCancel,
}: {
  jobId: string;
  onDone: (inv: EditableInvoice) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<BookItem[] | null>(null);
  const [item, setItem] = useState<BookItem | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The new line's id is held while the form is the same, so a retry after a lost answer adds it once.
  const save = useSaveKey(JSON.stringify([item?.id, quantity, price, reason]));
  useEffect(() => {
    if (item) return;
    let current = true;
    const t = setTimeout(() => {
      client
        .call<{ items: BookItem[] }>('pricebook.listItems', { query: query.trim() || undefined, limit: 8 })
        .then((r) => current && setFound(r.items))
        .catch((e: unknown) => current && setError(say(e)));
    }, 200);
    return () => {
      current = false;
      clearTimeout(t);
    };
  }, [query, item]);
  const cents = parseMoney(price);
  const changed = !!item && cents !== null && cents !== item.priceCents;
  const ok = !!item && quantityOk(quantity) && cents !== null && cents >= 0 && (!changed || !!reason.trim());
  const add = async () => {
    if (!item || cents === null) return;
    setBusy(true);
    setError(null);
    try {
      const inv = await client.call<EditableInvoice>('invoices.addLine', {
        jobId,
        lineId: save.key,
        pricebookItemId: item.id,
        quantity: quantity.trim(),
        ...(changed ? { unitPriceCents: cents, overrideReason: reason.trim() } : {}),
      });
      save.renew();
      onDone(inv);
    } catch (e) {
      setError(say(e));
      setBusy(false);
    }
  };
  return (
    <form
      className="inv-form"
      aria-label="Add a line"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok && !busy) void add();
      }}
    >
      {!item ? (
        <>
          <label className="f">
            Find it in the price book
            <input
              className="field"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name or code"
              autoFocus
            />
          </label>
          <ul className="inv-found" aria-label="Price book">
            {found?.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  className="inv-pick"
                  onClick={() => {
                    setItem(it);
                    setPrice(dollars(it.priceCents));
                  }}
                >
                  <span>
                    {it.name} <span className="muted small">{it.code}</span>
                  </span>
                  <span className="num">{money(it.priceCents)}</span>
                </button>
              </li>
            ))}
            {found && found.length === 0 && (
              <li className="muted small">Nothing in the price book matches.</li>
            )}
          </ul>
        </>
      ) : (
        <>
          <p>
            <b>{item.name}</b> <span className="muted small">{item.code}</span> · list{' '}
            {money(item.priceCents)}
          </p>
          <div className="inv-fields">
            <label className="f">
              Quantity
              <input
                className="field"
                inputMode="decimal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </label>
            <label className="f">
              Price each
              <input
                className="field"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </label>
          </div>
          {changed && (
            <label className="f">
              Why the price changes
              <input
                className="field"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Goodwill, a matched quote, a second visit…"
              />
            </label>
          )}
        </>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        {item && (
          <button className="btn primary" type="submit" disabled={!ok || busy}>
            {busy ? 'Adding…' : 'Add to the invoice'}
          </button>
        )}
        {item && (
          <button className="btn quiet" type="button" onClick={() => setItem(null)} disabled={busy}>
            Pick another
          </button>
        )}
        <button className="btn quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** A membership sold on this visit: today's list-priced lines at member prices, in one go (M8). */
function ApplyMemberPrices({ jobId, onDone }: { jobId: string; onDone: (inv: EditableInvoice) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = useSaveKey(`apply-member-prices:${jobId}`);
  return (
    <div className="row member-apply">
      <button
        className="btn primary"
        disabled={busy}
        onClick={() =>
          void (async () => {
            setBusy(true);
            try {
              onDone(
                await client.call<EditableInvoice>(
                  'invoices.applyMemberPrices',
                  { jobId },
                  { idempotencyKey: key.key },
                ),
              );
              key.renew();
              setError(null);
            } catch (e) {
              setError(say(e));
            } finally {
              setBusy(false);
            }
          })()
        }
      >
        Apply member prices to today’s lines
      </button>
      {error && <span className="error small">{error}</span>}
    </div>
  );
}
